import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { showToast } from '@/components/ui/sonner';
import { GetAllPlanQuery } from '@/Types/gql/graphql';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripePaymentElementOptions } from '@stripe/stripe-js';
import { LoaderCircle } from 'lucide-react';
import { useState } from 'react';

interface CheckoutFormProps {
  handleClose: () => void;
  selectedPlan: string;
  price: number;
  plans?: GetAllPlanQuery;
  setPaymentStatus: React.Dispatch<React.SetStateAction<string>>;
  setSelectedPlan: React.Dispatch<React.SetStateAction<string>>;
}

export default function CheckoutForm({ handleClose, setPaymentStatus, price }: CheckoutFormProps) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) {
      console.error('Stripe or Elements not loaded.');
      return;
    }
    setIsLoading(true);
    try {
      await elements.submit();
      const { error } = await stripe.confirmPayment({
        redirect: 'always',
        elements,
        confirmParams: {
          ['return_url']: `${window.location.origin}/thank-you-purchase`,
        },
      });
      if (error) {
        const errorMessage =
          error.type === 'card_error' || error.type === 'validation_error'
            ? error.message
            : 'An unexpected error occurred';
        showToast({ message: errorMessage, variant: 'error' });
        return;
      }
      setPaymentStatus('completed');
    } catch (err) {
      console.error('Payment submission error:', err);
      showToast({ message: 'An unexpected error occurred', variant: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const paymentElementOptions: StripePaymentElementOptions = {
    layout: 'accordion',
  };

  return (
    <form id='payment-form' onSubmit={handleSubmit}>
      <div className='py-5 px-7'>
        <PaymentElement id='payment-element' options={paymentElementOptions} />
      </div>
      <div className='z-20 w-full bg-[#151525] sticky bottom-0'>
        <div className='flex flex-col gap-3 px-7'>
          <Separator className='bg-[#FFFFFF21]' />
          <div className='flex items-center justify-between'>
            <h5 className='text-lg'>Grand Total</h5>
            <h4 className='text-[#E9CC7C]'>${price.toFixed(2)}</h4>
          </div>
          <div className='flex items-center justify-end gap-4 w-full pb-5'>
            <Button
              id='schedule-session-cancel-btn'
              variant='secondary'
              onClick={() => handleClose()}
            >
              Cancel
            </Button>
            <Button type='submit' disabled={isLoading || !stripe || !elements} id='submit'>
              <span id='button-text' className='font-bold'>
                {isLoading ? (
                  <LoaderCircle className='h-4 w-4 animate-spin' />
                ) : (
                  `Pay $${price.toFixed(2)}`
                )}
              </span>
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
}
